import Phaser from "phaser";
import { assetUrl, preloadAssets, spriteSheets, AssetKeys } from "../assets";
import { ENERGY_PICKUP_FEEDBACK_CONFIG } from "../energyPickupFeedbackConfig";
import { WOLF_ANIMATION_CONFIG } from "../systems/WolfAnimationSystem";
import { WOLF_FEEL_CONFIG } from "../wolfFeelConfig";
import { USE_WOLF_V2, WOLF_V2_ANIM_MAP } from "../wolfV2Config";

export class BootScene extends Phaser.Scene {
  constructor() {
    super("BootScene");
  }

  preload() {
    // Real asset-load progress -> start-panel loading bar (OverlayUi listens).
    this.load.on("progress", (value: number) => this.game.events.emit("boot:progress", value));
    // A failed file does not stall the loader (it still emits 'complete'); just
    // leave a trace so a missing asset is diagnosable.
    this.load.on("loaderror", (file: Phaser.Loader.File) => console.warn(`[BootScene] Asset no cargado: ${file.key} (${file.url})`));

    preloadAssets.forEach(([key, path]) => this.load.image(key, assetUrl(path)));
    spriteSheets.forEach(([key, path]) => {
      this.load.spritesheet(key, assetUrl(path), { frameWidth: 512, frameHeight: 512 });
    });

    // ── Load every V2 sheet that has been declared with a valid frameCount ──
    // When a new V2 animation is ready, add it to WOLF_V2_ANIM_MAP with its
    // frameCount and this loop will pick it up automatically.
    Object.values(ENERGY_PICKUP_FEEDBACK_CONFIG.assets).forEach((entry) => {
      this.load.spritesheet(entry.key, `${ENERGY_PICKUP_FEEDBACK_CONFIG.basePath}${entry.file}`, {
        frameWidth: ENERGY_PICKUP_FEEDBACK_CONFIG.frameWidth,
        frameHeight: ENERGY_PICKUP_FEEDBACK_CONFIG.frameHeight
      });
    });

    if (USE_WOLF_V2) {
      for (const [state, entry] of Object.entries(WOLF_V2_ANIM_MAP)) {
        if (entry.frameCount > 0) {
          const assetKey = this.getV2AssetKey(state);
          this.load.spritesheet(assetKey, entry.url, {
            frameWidth: entry.frameWidth ?? WOLF_ANIMATION_CONFIG.frameWidth,
            frameHeight: entry.frameHeight ?? WOLF_ANIMATION_CONFIG.frameHeight
          });
        }
      }
    }
  }

  create() {
    this.createWolfGlowTexture();
    this.createAimBoosterTexture();

    // ── Register V1 fallback animations (always present) ───────────────────
    this.anims.create({ key: "wolf-run", frames: this.anims.generateFrameNumbers(AssetKeys.wolf.run, { start: 0, end: 1 }), frameRate: 14, repeat: -1 });
    this.anims.create({ key: "wolf-left", frames: this.anims.generateFrameNumbers(AssetKeys.wolf.runLeft, { start: 0, end: 3 }), frameRate: 10, repeat: -1 });
    this.anims.create({ key: "wolf-right", frames: this.anims.generateFrameNumbers(AssetKeys.wolf.runRight, { start: 0, end: 3 }), frameRate: 10, repeat: -1 });
    this.anims.create({ key: "wolf-champion", frames: this.anims.generateFrameNumbers(AssetKeys.wolf.champion, { start: 0, end: 3 }), frameRate: 12, repeat: -1 });

    // ── Register every V2 animation declared in the config map ─────────────
    if (USE_WOLF_V2) {
      this.registerAllV2Animations();
    }
    this.registerPack11Animations();

    this.scene.start("RunScene");
    this.scene.launch("HudScene");
  }

  // ── V2 Registration Helpers ───────────────────────────────────────────────

  /**
   * Iterates WOLF_V2_ANIM_MAP and registers each declared V2 animation.
   * Only entries with frameCount > 0 and a loaded, validated texture are
   * registered. All others are silently skipped — the V1 fallback will be
   * used automatically by WolfAnimationSystem.
   */
  private registerAllV2Animations() {
    for (const [state, entry] of Object.entries(WOLF_V2_ANIM_MAP)) {
      if (entry.frameCount <= 0) continue;
      if (this.anims.exists(entry.v2Key)) continue;

      const assetKey = this.getV2AssetKey(state);
      if (!this.textures.exists(assetKey)) continue;
      if (!this.isV2TextureValid(assetKey, entry.expectedWidth, entry.expectedHeight)) continue;

      this.anims.create({
        key: entry.v2Key,
        frames: this.anims.generateFrameNumbers(assetKey, { start: 0, end: entry.frameCount - 1 }),
        frameRate: WOLF_FEEL_CONFIG.animationFps,
        repeat: -1
      });
    }
  }

  /**
   * Returns the Phaser texture cache key for a V2 animation state.
   * Uses a consistent naming convention: "wolf.v2.<state>".
   */
  private getV2AssetKey(state: string): string {
    return `wolf.v2.${state}`;
  }

  private isV2TextureValid(assetKey: string, expectedWidth: number, expectedHeight: number): boolean {
    const source = this.textures.get(assetKey).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
    const valid = source.width === expectedWidth && source.height === expectedHeight;
    if (!valid) {
      console.warn(
        `Wolf V2 [${assetKey}] ignored: expected ${expectedWidth}x${expectedHeight}, got ${source.width}x${source.height}.`
      );
    }
    return valid;
  }

  // ── Runtime Texture Generators ────────────────────────────────────────────

  private registerPack11Animations() {
    Object.values(ENERGY_PICKUP_FEEDBACK_CONFIG.assets).forEach((entry) => {
      if (this.anims.exists(entry.animationKey) || !this.textures.exists(entry.key)) return;
      this.anims.create({
        key: entry.animationKey,
        frames: this.anims.generateFrameNumbers(entry.key, { start: 0, end: entry.frames - 1 }),
        frameRate: entry.fps,
        repeat: entry.repeat
      });
    });
  }

  private createWolfGlowTexture() {
    if (this.textures.exists(AssetKeys.vfx.wolfGlow)) return;
    const texture = this.textures.createCanvas(AssetKeys.vfx.wolfGlow, 256, 256);
    const context = texture?.getContext();
    if (!texture || !context) return;

    const gradient = context.createRadialGradient(128, 128, 18, 128, 128, 126);
    gradient.addColorStop(0, "rgba(255,255,255,0.42)");
    gradient.addColorStop(0.34, "rgba(255,255,255,0.18)");
    gradient.addColorStop(0.72, "rgba(255,255,255,0.055)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    context.clearRect(0, 0, 256, 256);
    context.fillStyle = gradient;
    context.fillRect(0, 0, 256, 256);
    texture.refresh();
  }

  private createAimBoosterTexture() {
    if (this.textures.exists(AssetKeys.vfx.aimBooster) || !this.textures.exists(AssetKeys.vfx.aimIsotypeSource)) return;

    const source = this.textures.get(AssetKeys.vfx.aimIsotypeSource).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
    const scan = document.createElement("canvas");
    scan.width = source.width;
    scan.height = source.height;
    const scanContext = scan.getContext("2d", { willReadFrequently: true });
    if (!scanContext) return;

    scanContext.drawImage(source, 0, 0);
    const pixels = scanContext.getImageData(0, 0, scan.width, scan.height).data;
    let minX = scan.width;
    let minY = scan.height;
    let maxX = 0;
    let maxY = 0;

    for (let y = 0; y < scan.height; y += 2) {
      for (let x = 0; x < scan.width; x += 2) {
        const alpha = pixels[(y * scan.width + x) * 4 + 3];
        if (alpha <= 12) continue;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }

    if (maxX <= minX || maxY <= minY) return;

    const pad = 36;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(scan.width, maxX + pad);
    maxY = Math.min(scan.height, maxY + pad);
    const cropWidth = maxX - minX;
    const cropHeight = maxY - minY;
    const size = 256;
    const texture = this.textures.createCanvas(AssetKeys.vfx.aimBooster, size, size);
    const context = texture?.getContext();
    if (!texture || !context) return;

    const scale = Math.min((size * 0.86) / cropWidth, (size * 0.86) / cropHeight);
    const drawWidth = cropWidth * scale;
    const drawHeight = cropHeight * scale;
    context.clearRect(0, 0, size, size);
    context.drawImage(source, minX, minY, cropWidth, cropHeight, (size - drawWidth) * 0.5, (size - drawHeight) * 0.5, drawWidth, drawHeight);
    texture.refresh();
  }
}
